import { useEffect, useState, type PropsWithChildren } from 'react';

import { cx } from '@/utils/cx';

import type { BaseProps } from './base-props';

import { Input } from '../input/input';
import { Slider } from '../slider/slider';

export interface SliderInputProps<TFormData> extends BaseProps<TFormData> {
  type: 'slider';
  minValue: number;
  maxValue: number;
  step: number;
  /** Shown beside the field label (e.g. "$50", "30 min"). */
  formatDisplayValue?: (value: number) => string;
  /** Rounds/clamps raw slider values before updating the field. */
  snapValue?: (raw: number) => number;
  labelFormatter?: (value: number) => string;
  isDisabled?: boolean;
  isRequired?: boolean;
  className?: string;
  labelClassName?: string;
  withNumericInput?: boolean;
}

export function SliderFormInput<T>({
  children,
  rawFieldValue,
  onChange,
  onBlur,
  formatDisplayValue = String,
  ...props
}: PropsWithChildren<SliderInputProps<T>> & {
  rawFieldValue: number;
  onChange: (value: number) => void;
  onBlur?: () => void;
}) {
  const numericValue =
    typeof rawFieldValue === 'number' ? rawFieldValue : props.minValue;
  const value = props.snapValue ? props.snapValue(numericValue) : numericValue;
  const displayValue = formatDisplayValue?.(value) ?? value;

  return (
    <div className={props.className}>
      {props.label && (
        <div className="mb-3 flex items-center justify-between gap-3">
          {props.label && (
            <label
              className={cx(
                'text-xs font-medium text-quaternary',
                props.labelClassName,
              )}
            >
              {props.label}
              {props.isRequired ? ' *' : ''}
            </label>
          )}
          {props.withNumericInput ? (
            <NumericInput
              value={value}
              minValue={props.minValue}
              maxValue={props.maxValue}
              isDisabled={props.isDisabled}
              formatDisplayValue={formatDisplayValue}
              label={props.label}
              snapValue={props.snapValue}
              onCommit={onChange}
              onBlur={onBlur}
            />
          ) : (
            <span className="text-sm font-semibold text-primary">
              {displayValue}
            </span>
          )}
        </div>
      )}
      <Slider
        minValue={props.minValue}
        maxValue={props.maxValue}
        step={props.step}
        value={value}
        onChange={(nextValue: number | number[]) => {
          const raw = Array.isArray(nextValue)
            ? (nextValue[0] ?? props.minValue)
            : nextValue;
          const next = props.snapValue ? props.snapValue(raw) : raw;
          onChange(next);
        }}
        onChangeEnd={onBlur}
        labelPosition="default"
        labelFormatter={props.labelFormatter}
        aria-label={props.label}
        isDisabled={props.isDisabled}
      />
      {children}
    </div>
  );
}

function NumericInput({
  value,
  minValue,
  maxValue,
  isDisabled,
  formatDisplayValue,
  label,
  snapValue,
  onCommit,
  onBlur,
}: {
  value: number;
  minValue: number;
  maxValue: number;
  isDisabled?: boolean;
  formatDisplayValue: (value: number) => string;
  label?: string;
  snapValue?: (raw: number) => number;
  onCommit: (value: number) => void;
  onBlur?: () => void;
}) {
  const [draft, setDraft] = useState<string>(() => formatDisplayValue(value));
  const [isFocused, setIsFocused] = useState(false);

  // Keep draft in sync with external value when not actively editing
  // (e.g. when the slider moves).
  useEffect(() => {
    if (!isFocused) setDraft(formatDisplayValue(value));
  }, [value, isFocused, formatDisplayValue]);

  return (
    <Input
      type="text"
      inputMode="decimal"
      aria-label={label}
      value={draft}
      isDisabled={isDisabled}
      onChange={setDraft}
      onFocus={() => {
        setIsFocused(true);
        // Show the raw number while editing so users aren't fighting the formatter.
        setDraft(String(value));
      }}
      onBlur={() => {
        setIsFocused(false);
        const parsed = asNumber(draft);
        const committed = snapValue
          ? snapValue(parsed)
          : Math.min(maxValue, Math.max(minValue, parsed));
        setDraft(formatDisplayValue(committed));
        if (committed !== value) onCommit(committed);
        onBlur?.();
      }}
      size="xs"
      className="max-w-20"
    />
  );
}

const asNumber = (value: string | number): number => {
  if (typeof value === 'number') return value;
  // Strip commas/currency/units; preserve sign + decimal point (including ".5").
  const match = value.replaceAll(',', '').match(/-?(?:\d+\.?\d*|\.\d+)/);
  if (!match) return 0;
  const n = Number(match[0]);
  return Number.isNaN(n) ? 0 : n;
};
